import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { ensurePushSubscription } from "@/lib/push";

/**
 * Notification bell for professional-logins. Polls
 * `/api/notifications/new-appointments` every 60s and pops a toast on the
 * first render if unseen items exist. Clicking the bell shows a small
 * dropdown; clicking an item jumps to the Agenda for that day and calls
 * `/notifications/mark-seen` so the count clears.
 */
export default function NotifBell({ enabled = true }) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushStatus, setPushStatus] = useState("unknown"); // 'subscribed'|'permission-default'|'denied'|'unsupported'|'unknown'
  const alertedRef = useRef(false);
  const nav = useNavigate();

  // Try to silently re-subscribe on mount if the user already granted
  // permission on this device. This heals subscriptions that expire.
  useEffect(() => {
    if (!enabled) return;
    (async () => {
      const r = await ensurePushSubscription({ requestPermission: false });
      setPushStatus(r.status);
    })();
  }, [enabled]);

  const enablePushNow = async () => {
    const r = await ensurePushSubscription({ requestPermission: true });
    setPushStatus(r.status);
    if (r.status === "subscribed") {
      toast.success("Notificações do celular ativadas!");
      try { await api.post("/notifications/test-push"); } catch {}
    } else if (r.status === "denied") {
      toast.error("Você bloqueou notificações. Ative nas configurações do navegador/celular para receber alertas.");
    } else if (r.status === "unsupported") {
      toast.error("Seu navegador não suporta notificações push. Instale o app na tela inicial (iOS 16.4+/Android).");
    } else {
      toast.error("Não foi possível ativar. Tente novamente após instalar o app na tela inicial.");
    }
  };

  const fetchNow = async () => {
    try {
      const { data } = await api.get("/notifications/new-appointments");
      setItems(data.items || []);
      setCount(data.count || 0);
      if (!alertedRef.current && (data.count || 0) > 0) {
        alertedRef.current = true;
        toast.info(`Você tem ${data.count} novo(s) agendamento(s)!`);
      }
    } catch {
      /* silent — pro may not have permission on some deploys */
    }
  };

  useEffect(() => {
    if (!enabled) return;
    fetchNow();
    const iv = setInterval(fetchNow, 60_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const markSeenAndClose = async () => {
    try { await api.post("/notifications/mark-seen"); } catch {}
    setItems([]); setCount(0); setOpen(false);
  };

  const goToItem = async (id, scheduled_at) => {
    setOpen(false);
    try { await api.post("/notifications/mark-seen"); } catch {}
    setItems([]); setCount(0);
    const day = new Date(scheduled_at).toISOString().slice(0, 10);
    nav(`/agenda?date=${day}#appt-${id}`);
  };

  if (!enabled) return null;

  return (
    <div className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen(!open)}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-muted transition-colors"
        aria-label="Notificações"
      >
        <Bell className="h-4 w-4" />
        {pushStatus === "subscribed" && count === 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-background" title="Push do celular ativo" />
        )}
        {count > 0 && (
          <span
            data-testid="notif-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="notif-panel"
            className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-lg border border-border bg-card shadow-xl z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="text-sm font-semibold">Novos agendamentos</div>
              {count > 0 && (
                <button
                  data-testid="notif-mark-seen"
                  onClick={markSeenAndClose}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Marcar como visto
                </button>
              )}
            </div>
            {pushStatus !== "subscribed" && pushStatus !== "unsupported" && (
              <div className="px-4 py-3 border-b border-border bg-accent/10 space-y-2">
                <div className="text-xs text-foreground/80 flex items-start gap-2">
                  <BellRing className="h-4 w-4 mt-0.5 shrink-0 text-accent-foreground" />
                  <span>
                    Ative as notificações do celular para receber alertas <strong>mesmo com o app fechado</strong>.
                    {" "}Instale este app na tela inicial primeiro (iOS 16.4+ / Android).
                  </span>
                </div>
                <button
                  data-testid="notif-enable-push"
                  onClick={enablePushNow}
                  className="w-full text-xs font-medium text-primary-foreground bg-primary hover:opacity-90 transition-opacity py-2 px-3 rounded-md"
                >
                  {pushStatus === "denied" ? "Como reativar" : "Ativar notificações no celular"}
                </button>
              </div>
            )}
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">Nada novo por aqui.</div>
              ) : (
                items.map((it) => (
                  <button
                    key={it.id}
                    data-testid={`notif-item-${it.id}`}
                    onClick={() => goToItem(it.id, it.scheduled_at)}
                    className="w-full text-left px-4 py-3 border-b border-border/60 hover:bg-muted transition-colors last:border-b-0"
                  >
                    <div className="text-sm font-medium truncate">{it.client_name || "Sem nome"}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {it.service_name || "—"} · {fmtDate(it.scheduled_at)} às {fmtTime(it.scheduled_at)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
